// ============================================================================
// A new subcontractor named while setting up a building — through the ONE door.
//
// Since item #683 (2026-10-03, TCSD-0368/0369) there is one way to create a sub:
// add_subcontractor(). A sub named on a new account's service line is a nightly
// sub on the bench (Ready); putting them on the building makes them Active and
// creates HR's onboarding card at that moment (sub_paperwork_on_first_building),
// not when the record is made. This helper used to call the retired
// create-provider-and-onboarding path, which made the card at creation and left
// 78 of 87 cards cancelled.
//
// Callers: the New Account SOP create modal, the service-lines editor.
// ============================================================================

import { supabase } from '../../shared/supabase';
import { rpcErrorMessage } from '../../shared/rpc-error';
import { alreadyOnFileFrom } from '../subcontractors/already-on-file';

export interface NewSubInput {
  /** Go-by name — the primary display name everywhere (partner_name). */
  name: string;
  /** Legal business name — appears on the contract (legal_business_name). Optional. */
  legalBusinessName?: string | null;
  phone?: string | null;
  email?: string | null;
  /** The building's market — a real market name from the market list. */
  marketRegion?: string | null;
  createdByEid?: string | null;
  /** The second press after "Already on file?": it's a different company. */
  addAnyway?: boolean;
}

export interface NewSubResult {
  providerId: string;
}

/** One key per typed sub, so a form remembers the subs it has already made. */
export function newSubKey(name: string, phone: string | null | undefined): string {
  return `${name.trim().toLowerCase()}|${(phone ?? '').replace(/[^0-9]/g, '')}`;
}

export async function createSubForBuilding(input: NewSubInput): Promise<NewSubResult> {
  const { data, error } = await supabase.rpc('add_subcontractor', {
    p_name: input.name.trim(),
    p_phone: input.phone?.trim() || '',
    p_coverage_markets: input.marketRegion ? [input.marketRegion] : [],
    p_capabilities: ['RECURRING'],
    p_status: 'Ready',
    p_email: input.email?.trim() || undefined,
    p_channel: 'AM',
    p_legal_business_name: input.legalBusinessName?.trim() || undefined,
    p_add_anyway: input.addAnyway ?? false,
  });
  if (error) {
    const found = alreadyOnFileFrom(error);
    if (found) throw found;
    throw new Error(rpcErrorMessage(error, 'Could not add the subcontractor.'));
  }
  const out = data as { provider_id?: string } | null;
  if (!out?.provider_id) throw new Error('The new subcontractor came back without an id.');
  return { providerId: out.provider_id };
}
